import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { showNotification } from 'components';
import {
  NotificationType,
  PermissionGroupDetailModel,
  PermissionGroupScopeInputModel,
  PermissionScopeCatalogueEntryModel,
} from 'models';
import { ScopeCode } from 'config/scopes';
import { menuConfigRaw } from 'config/menuConfig';
import { routes } from 'config/routes';
import { MenuConfigModel } from 'models/menuConfig.model';
import { isAxiosError } from 'utils/errors';
import { useScopeCatalogue, useUpdatePermissionGroup } from 'features/permissionGroups';
import { mapNotificationsToInput } from './PermissionGroupHeader';

enum PermissionGroupScopeErrors {
  EDIT_WITHOUT_VIEW = 'PERMISSION_GROUP_EDIT_WITHOUT_VIEW',
  DERIVED_SCOPE_PARENT_VIEW_MISSING = 'PERMISSION_GROUP_DERIVED_SCOPE_PARENT_VIEW_MISSING',
  UNKNOWN_SCOPE_CODE = 'PERMISSION_GROUP_UNKNOWN_SCOPE_CODE',
  LOCKOUT = 'PERMISSION_GROUP_LOCKOUT',
}

export interface ScopeGrant {
  view: boolean;
  edit: boolean;
}

export type ScopeGrantMap = Partial<Record<ScopeCode, ScopeGrant>>;

export interface ScopeRow {
  entry: PermissionScopeCatalogueEntryModel;
  label: string;
  children: PermissionScopeCatalogueEntryModel[];
}

export interface ScopeGroup {
  key: string;
  label: React.ReactNode | null;
  rows: ScopeRow[];
}

export interface ScopeSection {
  key: string;
  label: React.ReactNode;
  groups: ScopeGroup[];
}

const OTHER_SECTION_KEY = '__OTHER__';

/**
 * Pure state-update helper enforcing the view/edit coupling in both directions:
 * - Turning edit ON forces view ON as well.
 * - Turning view OFF forces edit OFF as well.
 * This is the single place the invariant is enforced so it cannot be bypassed by direct clicks.
 */
const applyGrantChange = (
  map: ScopeGrantMap,
  code: ScopeCode,
  field: 'view' | 'edit',
  value: boolean
): ScopeGrantMap => {
  const current = map[code] ?? { view: false, edit: false };

  let next: ScopeGrant;
  if (field === 'edit') {
    next = value ? { view: true, edit: true } : { view: current.view, edit: false };
  } else {
    next = value ? { view: true, edit: current.edit } : { view: false, edit: false };
  }

  return { ...map, [code]: next };
};

/**
 * Walks menuConfigRaw depth-first, building a ScopeCode -> menu path map (ordered list of
 * display-name nodes from the root down to, and including, the node that owns the scope).
 * Only PRIMARY-style menu entries (routes.tsx entries carrying a `scope`) are recorded here;
 * DERIVED scopes have no menu entry of their own and are resolved via their `dependsOn` parent.
 */
const buildScopeMenuPaths = (
  catalogue: PermissionScopeCatalogueEntryModel[]
): Partial<Record<ScopeCode, MenuConfigModel[]>> => {
  const routeScopeMap = new Map<string, ScopeCode>();
  routes.forEach((route) => {
    if (route.path && route.scope) {
      routeScopeMap.set(route.path, route.scope as ScopeCode);
    }
  });

  const result: Partial<Record<ScopeCode, MenuConfigModel[]>> = {};
  const pathsByRoute = new Map<string, MenuConfigModel[]>();

  const walk = (nodes: MenuConfigModel[] | undefined, ancestors: MenuConfigModel[]) => {
    nodes?.forEach((node) => {
      const path = [...ancestors, node];
      pathsByRoute.set(node.route, path);
      const scope = routeScopeMap.get(node.route);
      if (scope && !result[scope]) {
        result[scope] = path;
      }
      walk(node.children, path);
    });
  };

  walk(menuConfigRaw, []);

  // Some routes (e.g. a list row's detail page) are only reachable by drilling into another
  // screen and have no menu entry of their own. Group those under the nearest ancestor route
  // that *does* have a menu entry (found by stripping trailing path segments), appended as an
  // extra path level, so they land as a sub-group of that screen instead of falling into "Egyéb".
  routeScopeMap.forEach((scope, routePath) => {
    if (result[scope]) {
      return;
    }

    const segments = routePath.split('/');
    while (segments.length > 1 && !pathsByRoute.has(segments.join('/'))) {
      segments.pop();
    }

    const ancestorPath = pathsByRoute.get(segments.join('/'));
    if (ancestorPath) {
      result[scope] = [...ancestorPath, ancestorPath[ancestorPath.length - 1]];
    }
  });

  // A handful of PRIMARY scopes gate a capability embedded in another screen rather than a
  // route of their own (e.g. NEXON_ROLES backs a lookup used by the Munkavégzők screen;
  // READING_ORDER_DELETION gates a bulk-delete action on the Reading Unit Details screen). The
  // backend catalogue pins those via `groupWith`, naming the scope whose screen they belong to.
  catalogue.forEach((entry) => {
    const targetScope = entry.groupWith;
    const targetPath = targetScope && result[targetScope];
    if (!result[entry.code] && targetPath) {
      result[entry.code] = targetPath;
    }
  });

  return result;
};

/**
 * Sections mirror the three top-level modules shown on the landing page (e.g. Leolvasás,
 * Közös funkciók, Ügyfélcsere - path[1] in the menu path, path[0] being the HOME root).
 * Anything nested more than one level below the module (path.length > 3) is rendered as a
 * labelled sub-group inside that module's accordion instead of becoming its own top-level
 * section, so the accordion list on the page always matches the three landing-page modules.
 */
const buildSections = (
  catalogue: PermissionScopeCatalogueEntryModel[],
  otherLabel: React.ReactNode
): ScopeSection[] => {
  const scopeMenuPaths = buildScopeMenuPaths(catalogue);
  const byCode = new Map(catalogue.map((entry) => [entry.code, entry]));

  const sectionOrder: string[] = [];
  const sectionsByKey = new Map<string, { label: React.ReactNode; groupOrder: string[]; groupsByKey: Map<string, ScopeGroup> }>();
  const rowsByCode = new Map<ScopeCode, ScopeRow>();

  const getOrCreateSection = (key: string, label: React.ReactNode) => {
    let section = sectionsByKey.get(key);
    if (!section) {
      section = { label, groupOrder: [], groupsByKey: new Map() };
      sectionsByKey.set(key, section);
      sectionOrder.push(key);
    }
    return section;
  };

  const getOrCreateGroup = (
    section: { label: React.ReactNode; groupOrder: string[]; groupsByKey: Map<string, ScopeGroup> },
    key: string,
    label: React.ReactNode | null
  ): ScopeGroup => {
    let group = section.groupsByKey.get(key);
    if (!group) {
      group = { key, label, rows: [] };
      section.groupsByKey.set(key, group);
      section.groupOrder.push(key);
    }
    return group;
  };

  const primaryEntries = catalogue.filter((entry) => entry.kind !== 'DERIVED');
  const derivedEntries = catalogue.filter((entry) => entry.kind === 'DERIVED');

  primaryEntries.forEach((entry) => {
    const path = scopeMenuPaths[entry.code];

    if (!path || path.length === 0) {
      const section = getOrCreateSection(OTHER_SECTION_KEY, otherLabel);
      const group = getOrCreateGroup(section, OTHER_SECTION_KEY, null);
      const row: ScopeRow = { entry, label: entry.label, children: [] };
      group.rows.push(row);
      rowsByCode.set(entry.code, row);
      return;
    }

    // path[0] is the HOME root; path[1] is the module shown as a card on the landing page.
    const moduleNode = path.length > 1 ? path[1] : path[0];
    const moduleKey = moduleNode.route;
    const moduleLabel = <Trans i18nKey={moduleNode.displayName} />;
    const section = getOrCreateSection(moduleKey, moduleLabel);

    // Anything more than one level below the module gets a sub-group header named after its
    // immediate parent; screens sitting directly under the module get no header at all.
    const hasSubgroup = path.length > 3;
    const parent = hasSubgroup ? path[path.length - 2] : undefined;
    const groupKey = hasSubgroup ? path.slice(1, -1).map((n) => n.route).join('/') : moduleKey;
    const groupLabel = hasSubgroup && parent ? <Trans i18nKey={parent.displayName} /> : null;

    const group = getOrCreateGroup(section, groupKey, groupLabel);
    const row: ScopeRow = { entry, label: entry.label, children: [] };
    group.rows.push(row);
    rowsByCode.set(entry.code, row);
  });

  derivedEntries.forEach((entry) => {
    const parentRow = entry.dependsOn ? rowsByCode.get(entry.dependsOn) : undefined;

    if (parentRow) {
      parentRow.children.push(entry);
      return;
    }

    // Parent scope's menu path couldn't be resolved (or dependsOn missing) - fall back to Egyéb.
    const section = getOrCreateSection(OTHER_SECTION_KEY, otherLabel);
    const group = getOrCreateGroup(section, OTHER_SECTION_KEY, null);
    const row: ScopeRow = { entry, label: entry.label, children: [] };
    group.rows.push(row);
  });

  // Ensure "Egyéb" is always last, regardless of when it was first created.
  const orderedKeys = sectionOrder.filter((key) => key !== OTHER_SECTION_KEY);
  if (sectionsByKey.has(OTHER_SECTION_KEY)) {
    orderedKeys.push(OTHER_SECTION_KEY);
  }

  return orderedKeys
    .map((key) => {
      const section = sectionsByKey.get(key)!;
      const groups = section.groupOrder.map((groupKey) => section.groupsByKey.get(groupKey) as ScopeGroup);
      return { key, label: section.label, groups };
    })
    .filter((section) => {
      // Defensive: drop empty sections (shouldn't normally happen).
      return section.groups.some((group) => group.rows.length > 0) || byCode.size === 0;
    });
};

const buildInitialGrantMap = (
  catalogue: PermissionScopeCatalogueEntryModel[],
  scopes: PermissionGroupDetailModel['scopes']
): ScopeGrantMap => {
  const byCode = new Map(scopes.map((scope) => [scope.scopeCode, scope]));
  const map: ScopeGrantMap = {};

  catalogue.forEach((entry) => {
    const existing = byCode.get(entry.code);
    map[entry.code] = existing
      ? { view: existing.canView, edit: existing.canEdit }
      : { view: false, edit: false };
  });

  return map;
};

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  [PermissionGroupScopeErrors.EDIT_WITHOUT_VIEW]: `ERRORS.${PermissionGroupScopeErrors.EDIT_WITHOUT_VIEW}`,
  [PermissionGroupScopeErrors.DERIVED_SCOPE_PARENT_VIEW_MISSING]: `ERRORS.${PermissionGroupScopeErrors.DERIVED_SCOPE_PARENT_VIEW_MISSING}`,
  [PermissionGroupScopeErrors.UNKNOWN_SCOPE_CODE]: `ERRORS.${PermissionGroupScopeErrors.UNKNOWN_SCOPE_CODE}`,
  [PermissionGroupScopeErrors.LOCKOUT]: `ERRORS.${PermissionGroupScopeErrors.LOCKOUT}`,
};

/**
 * Owns the scope-grant editing state for a permission group, shared between the sticky
 * save button (rendered next to the group header) and the accordion table further down
 * the page.
 */
export const usePermissionGroupScopes = (group: PermissionGroupDetailModel) => {
  const { t } = useTranslation();
  const { data: catalogue } = useScopeCatalogue();
  const { mutateAsync: updatePermissionGroup, isPending } = useUpdatePermissionGroup();

  // Defensive filter: the backend already excludes LOOKUP scopes, but never render them as rows.
  const grantableCatalogue = useMemo(
    () => (catalogue ?? []).filter((entry) => entry.kind !== 'LOOKUP'),
    [catalogue]
  );

  const initialGrants = useMemo(
    () => buildInitialGrantMap(grantableCatalogue, group.scopes),
    [grantableCatalogue, group.scopes]
  );

  // ScopeCode -> its DERIVED children, so turning a parent's view off can clear them too.
  const childrenByParent = useMemo(() => {
    const map = new Map<ScopeCode, ScopeCode[]>();
    grantableCatalogue.forEach((entry) => {
      if (entry.kind === 'DERIVED' && entry.dependsOn) {
        const parentCode = entry.dependsOn as ScopeCode;
        map.set(parentCode, [...(map.get(parentCode) ?? []), entry.code]);
      }
    });
    return map;
  }, [grantableCatalogue]);

  const [grants, setGrants] = useState<ScopeGrantMap>(initialGrants);
  const [initialGrantsSnapshot, setInitialGrantsSnapshot] = useState<ScopeGrantMap>(initialGrants);

  // Re-seed local state whenever the underlying data changes (e.g. after a save invalidation).
  const catalogueKey = grantableCatalogue.map((entry) => entry.code).join(',');
  const scopesKey = group.scopes.map((scope) => `${scope.scopeCode}:${scope.canView}:${scope.canEdit}`).join(',');
  const [lastSyncKey, setLastSyncKey] = useState(`${catalogueKey}|${scopesKey}`);
  if (lastSyncKey !== `${catalogueKey}|${scopesKey}`) {
    setGrants(initialGrants);
    setInitialGrantsSnapshot(initialGrants);
    setLastSyncKey(`${catalogueKey}|${scopesKey}`);
  }

  const sections = useMemo(
    () => buildSections(grantableCatalogue, <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.SCOPE_SECTION_OTHER" />),
    [grantableCatalogue]
  );

  const handleGrantChange = (code: ScopeCode, field: 'view' | 'edit', value: boolean) => {
    setGrants((prev) => {
      let next = applyGrantChange(prev, code, field, value);

      if (field === 'view' && !value) {
        (childrenByParent.get(code) ?? []).forEach((childCode) => {
          next = applyGrantChange(next, childCode, 'view', false);
        });
      }

      return next;
    });
  };

  const isDirty = useMemo(() => {
    const codes = new Set([...Object.keys(grants), ...Object.keys(initialGrantsSnapshot)]);
    return Array.from(codes).some((code) => {
      const a = grants[code as ScopeCode] ?? { view: false, edit: false };
      const b = initialGrantsSnapshot[code as ScopeCode] ?? { view: false, edit: false };
      return a.view !== b.view || a.edit !== b.edit;
    });
  }, [grants, initialGrantsSnapshot]);

  const handleSave = async () => {
    const scopesPayload: PermissionGroupScopeInputModel[] = grantableCatalogue.map((entry) => {
      const grant = grants[entry.code] ?? { view: false, edit: false };
      return { code: entry.code, view: grant.view, edit: grant.edit };
    });

    try {
      await updatePermissionGroup({
        id: group.id,
        data: {
          name: group.name,
          isActive: group.isActive,
          scopes: scopesPayload,
          notifications: mapNotificationsToInput(group),
        },
      });
      setInitialGrantsSnapshot(grants);
    } catch (error) {
      if (isAxiosError(error)) {
        const code = error.response?.data?.message;
        const key = code ? ERROR_MESSAGE_KEYS[code] : undefined;
        if (key) {
          showNotification({ content: t(key), type: NotificationType.ERROR });
          return;
        }
      }
      throw error;
    }
  };

  return { sections, grants, handleGrantChange, isDirty, isPending, handleSave };
};
